import { Color } from '@coast/schemas';
import { ClassValue } from 'clsx';
import { isNil, isNumber, isString } from 'lodash';
import { CSSProperties, ReactNode } from 'react';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { Colors } from '@shared/styles';
import styles from './Pill.module.css';

export type PillColor = Color | 'white' | 'transparent' | 'backgroundGrayLight';

interface Props {
  children?: ReactNode;
  sections?: ReactNode[];
  noBorder?: boolean;
  backgroundColor?: PillColor;
  round?: boolean;
  maxWidth?: CSSProperties['maxWidth'];
  numberOfLines?: number;
  onRemove?(): void;
  italic?: boolean;
  noLeftMargin?: boolean;
  noRightMargin?: boolean;
  borderColor?: string;
  borderWidth?: number;
  className?: ClassValue;
  style?: CSSProperties;
  textStyle?: CSSProperties;
  onPress?: VoidFunction;
  loading?: boolean;
  strikeThrough?: boolean;
}

export function Pill({
  children,
  sections = [children],
  backgroundColor = 'backgroundGrayLight',
  round = false,
  noBorder = false,
  maxWidth = '256px',
  numberOfLines = 1,
  onRemove,
  italic,
  noLeftMargin = false,
  noRightMargin = false,
  borderColor,
  borderWidth,
  className,
  style,
  textStyle,
  onPress,
  loading = false,
  strikeThrough = false,
}: Readonly<Props>) {
  const isOneLine = numberOfLines === 1;
  const pressable = !loading && !isNil(onPress);

  return (
    <View
      data-slot="pill"
      className={[
        styles.pill,
        pressable && styles.pressable,
        italic && styles.italic,
        round && styles.round,
        noBorder && styles.noBorder,
        noLeftMargin && styles.noLeftMargin,
        noRightMargin && styles.noRightMargin,
        isOneLine && styles.oneLine,
        className,
      ]}
      style={{
        backgroundColor: Colors.v2[backgroundColor],
        ...(!isNil(borderColor) && { borderColor }),
        ...(!isNil(borderWidth) && { borderWidth }),
        ...style,
      }}
      onClick={pressable ? onPress : undefined}
    >
      {loading && (
        <View className={styles.loading}>
          <LoadingIndicator />
        </View>
      )}
      {sections.map((section, index) => (
        <PillSection
          key={index}
          strikeThrough={strikeThrough}
          className={[styles.section, index > 0 && styles.notFirst, loading && styles.disabled]}
          maxWidth={maxWidth}
          italic={italic}
          numberOfLines={numberOfLines}
          textStyle={textStyle}
        >
          {section}
        </PillSection>
      ))}
      {onRemove && (
        <Button
          stopPropagation
          onClick={!loading ? onRemove : undefined}
          variant="ghost"
          size="small"
          icon="exit"
          iconGridSize={2}
          iconOnly
          className={styles.removeButton}
        />
      )}
    </View>
  );
}

function PillSection({
  children,
  numberOfLines,
  italic,
  strikeThrough,
  className,
  maxWidth,
  textStyle,
}: Pick<Props, 'children' | 'numberOfLines' | 'italic' | 'strikeThrough' | 'textStyle'> & {
  className: (string | false)[];
  maxWidth: CSSProperties['maxWidth'];
}) {
  const isPrimitiveChild = isString(children) || isNumber(children);

  return (
    <View
      className={className}
      style={{ maxWidth }}
      title={isPrimitiveChild ? String(children) : undefined}
    >
      {isPrimitiveChild && (
        <Text
          flex
          size="body1"
          numberOfLines={numberOfLines}
          italic={italic}
          lineThrough={strikeThrough}
          style={textStyle}
          color={italic ? Colors.v2.gray50 : undefined}
        >
          {children}
        </Text>
      )}
      {!isPrimitiveChild && children}
    </View>
  );
}
